import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useDeliveryDetailService from '../../../app/services/hooks/useDeliveryDetailService';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { DeliveryDetail } from '../../../app/models/DeliveryDetail';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

import DefaultCard from '../../../components/default/DefaultCard';
import DefaultModal from '../../../components/default/DefaultModal';
import DefaultToolBar from '../../../components/default/DefaultToolBar';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import DeliveryDetailDataTable from './tables/DeliveryDetailDataTable';
import DeliveryDetailCreate from './DeliveryDetailCreate';
import DeliveryDetailEdit from './DeliveryDetailEdit';
import LazyLoading from '../../../components/LazyLoading';
import DefaultDataTable from '../../../components/default/DefaultDataTable';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';

interface Props {
    deliveryId: number;
    isDelivered?: boolean;
    onUpdate?: () => void;
}

const DeliveryDetailContainer = ({ deliveryId, isDelivered = false, onUpdate }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    
    const {
        getDeliveryDetails,
        deleteDeliveryDetail,
        fetchingGetDeliveryDetails,
        fetchingDeleteDeliveryDetail
    } = useDeliveryDetailService();
    
    const { showDelivery } = useDeliveryService();

    // Estados
    const [details, setDetails] = useState<DeliveryDetail[]>([]);
    const [showingLots, setShowingLots] = useState(false);
    const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
    const [showingCreate, setShowingCreate] = useState(false);
    const [showingEdit, setShowingEdit] = useState(false);
    const [detailIdEdit, setDetailIdEdit] = useState<number>(-1);

    useEffect(() => {
        reloadDetails();
    }, [deliveryId]);

    const reloadDetails = () => {
        getDeliveryDetails(deliveryId, {
            onSuccess: (response: ServiceResponse) => {
                setDetails(response.data?.delivery_details || []);
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar detalles:', response);
                toast.error(response.message, { autoClose: 2000 });
                setDetails([]);
            }
        });
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
        reloadDetails();
        if (onUpdate) onUpdate();
    };

    const showEdit = (detailId: number) => {
        setDetailIdEdit(detailId);
        setShowingEdit(true);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setDetailIdEdit(-1);
        reloadDetails();
        if (onUpdate) onUpdate();
    };

    const destroy = (detailId: number) => {
        const _text = 'Está a punto de eliminar el detalle #' + detailId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando detalle...');
                        
                        deleteDeliveryDetail(deliveryId, detailId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                
                                reloadDetails();
                                if (onUpdate) onUpdate();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                
                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El detalle no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const grouped = (() => {
        const map = new Map<string, { key: string; product: string; unit: string; total_quantity: number; lots: DeliveryDetail[] }>();
        for (const d of details) {
            const product = d.product || '—';
            const key = `${d.sales_order_detail_id ?? ''}::${product}`;
            const prev = map.get(key);
            const qty = Number(d.quantity ?? 0) || 0;
            if (!prev) {
                map.set(key, { key, product, unit: d.unit || '', total_quantity: qty, lots: [d] });
            } else {
                prev.total_quantity += qty;
                prev.lots.push(d);
            }
        }
        return Array.from(map.values());
    })();

    const openLots = (rowKey: string) => {
        setSelectedGroupKey(rowKey);
        setShowingLots(true);
    };

    const closeLots = () => {
        setShowingLots(false);
        setSelectedGroupKey(null);
    };

    const selectedLots = grouped.find(g => g.key === selectedGroupKey)?.lots ?? [];
    const noopSetFilter = () => undefined;

    const destroyGroup = (rowKey: string) => {
        const group = grouped.find(g => g.key === rowKey);
        const lots = group?.lots ?? [];
        const productName = group?.product ?? 'producto';
        if (lots.length === 0) return;

        const _text = `Está a punto de eliminar ${lots.length} lote(s) del producto "${productName}" en esta entrega.`;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando lotes...');

                        const ids = lots
                            .map(l => l.id)
                            .filter((v): v is number => typeof v === 'number' && v > 0);

                        const deleteNext = (idx: number) => {
                            if (idx >= ids.length) {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: 'Lotes eliminados correctamente.'
                                });
                                if (showingLots && selectedGroupKey === rowKey) {
                                    closeLots();
                                }
                                reloadDetails();
                                if (onUpdate) onUpdate();
                                return;
                            }

                            deleteDeliveryDetail(deliveryId, ids[idx], {
                                onSuccess: () => deleteNext(idx + 1),
                                onError: (resp: ServiceResponse) => {
                                    if (hideLoading) hideLoading();
                                    useReactConfirmAlert().errorAlert({
                                        title: 'Error',
                                        message: resp.message || 'Error al eliminar lotes.'
                                    });
                                    reloadDetails();
                                }
                            });
                        };

                        deleteNext(0);
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'No se eliminaron los lotes.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <DefaultCard>
                {!isDelivered && (
                    <DefaultToolBar
                        left={
                            <ButtonCreate 
                                callbackCreate={showCreate} 
                                title="Nueva Posición" 
                            />
                        }
                    />
                )}

                {fetchingGetDeliveryDetails ? (
                    <LazyLoading height={300} />
                ) : (
                    <DeliveryDetailDataTable
                        details={grouped}
                        loading={fetchingGetDeliveryDetails}
                        viewLots={openLots}
                        destroyGroup={isDelivered ? undefined : destroyGroup}
                    />
                )}
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Detalle de Entrega"
                    backdrop={true}
                    showFooter={false}
                    size="lg"
                >
                    <DeliveryDetailCreate
                        deliveryId={deliveryId}
                        onSaved={hideCreate}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            )}

            {showingEdit && detailIdEdit > 0 && (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Detalle de Entrega"
                    backdrop={true}
                    showFooter={false}
                    size="lg"
                >
                    <DeliveryDetailEdit
                        deliveryId={deliveryId}
                        detailId={detailIdEdit}
                        onSaved={hideEdit}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            )}

            {showingLots && (
                <DefaultModal
                    show={showingLots}
                    handleClose={closeLots}
                    title="Lotes asociados"
                    backdrop={true}
                    showFooter={false}
                    size="lg"
                >
                    <DefaultDataTable
                        columns={[
                            {
                                name: 'Lote',
                                selector: (row: DeliveryDetail) => row.batch || '',
                                sortable: false,
                                cell: (row: DeliveryDetail) => <span>{row.batch || row.batch_name || `Lote #${row.batch_id}`}</span>
                            },
                            {
                                name: 'Cantidad',
                                selector: (row: DeliveryDetail) => row.quantity || 0,
                                sortable: false,
                                width: '120px',
                                cell: (row: DeliveryDetail) => <span>{row.quantity ?? 0}</span>
                            },
                            {
                                name: 'Acciones',
                                selector: (row: DeliveryDetail) => row.id,
                                sortable: false,
                                width: isDelivered ? '60px' : '140px',
                                cell: (row: DeliveryDetail) => (
                                    <div className="">
                                        {!isDelivered && (
                                            <>
                                                <ButtonTableAction
                                                    callbackFunction={() => showEdit(row.id ?? -1)}
                                                    classIcon={'mdi mdi-pencil'}
                                                    colorIcon={'text-warning'}
                                                    errorMessage={'No se puede editar este registro.'}
                                                    title={'Editar'}
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => destroy(row.id ?? -1)}
                                                    classIcon={'mdi mdi-delete'}
                                                    colorIcon={'text-danger'}
                                                    errorMessage={'No se puede eliminar este registro.'}
                                                    title={'Eliminar'}
                                                />
                                            </>
                                        )}
                                        {isDelivered && (
                                            <span className="text-muted" style={{ fontSize: '0.8em' }}>—</span>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                        data={selectedLots}
                        progressPending={fetchingDeleteDeliveryDetail}
                        paginationTotalRows={0}
                        filter={{ page: 1, per_page: 1000, sort: '', order: '' }}
                        setFilter={noopSetFilter}
                        pagination={false}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default DeliveryDetailContainer;
